import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"

/**
 * Phase 2.8 Tier 2 — renders a single chart block on the report
 * detail / preview page from a `chart_data` payload.
 *
 * The runner emits one entry per block keyed `chart_<idx>`. The
 * controller reads the block's id from the data attribute and
 * renders into the canvas target. Up to 4 blocks per report (cap
 * enforced by the model).
 *
 * Re-renders cleanly on `chart-data` value changes (controller
 * destroys + re-instantiates) so the live builder preview stays
 * in sync as the user tweaks group/value/aggregate.
 */
export default class extends Controller {
  static targets = ["canvas"]
  static values = {
    kind: { type: String, default: "bar" },
    chartData: Object,
    title: String,
    // Optional accent color(s) prepended to the default palette —
    // used by Ask AI dashboard cards so a card's accent drives its
    // chart color. Empty array = default palette unchanged.
    palette: Array
  }

  connect() {
    this._render()
  }

  chartDataValueChanged() {
    this._render()
  }

  disconnect() {
    if (this._chart) this._chart.destroy()
    this._chart = null
  }

  _render() {
    if (!this.hasCanvasTarget) return
    if (this._chart) {
      this._chart.destroy()
      this._chart = null
    }
    const data = this.chartDataValue || { labels: [], datasets: [] }
    if (!data.labels || data.labels.length === 0) {
      this.canvasTarget.style.display = "none"
      return
    }
    this.canvasTarget.style.display = ""

    const kind = this.kindValue
    const cfg = this._configFor(kind, data)
    this._chart = new Chart(this.canvasTarget, cfg)
  }

  _configFor(kind, data) {
    const defaults = [
      "#2563eb", "#dc2626", "#16a34a", "#f59e0b",
      "#9333ea", "#0891b2", "#ea580c", "#65a30d"
    ]
    const accent = (this.paletteValue || []).filter(Boolean)
    const palette = accent.length ? [...accent, ...defaults.filter((c) => !accent.includes(c))] : defaults
    const datasets = (data.datasets || []).map((ds, i) => ({
      ...ds,
      backgroundColor: kind === "pie"
        ? data.labels.map((_, idx) => palette[idx % palette.length])
        : palette[i % palette.length],
      borderColor: kind === "line" ? palette[i % palette.length] : "transparent",
      borderWidth: kind === "line" ? 2 : 0,
      borderRadius: (kind === "bar" || kind === "stacked_bar") ? 3 : undefined,
      maxBarThickness: (kind === "bar" || kind === "stacked_bar") ? 32 : undefined,
      fill: kind === "line" ? false : undefined,
      pointRadius: kind === "line" ? 0 : undefined,
      pointHoverRadius: kind === "line" ? 4 : undefined,
      tension: kind === "line" ? 0.15 : undefined
    }))

    const xTitle = data.x_label ? { display: true, text: data.x_label, font: { size: 12 } } : { display: false }
    const yTitle = data.y_label ? { display: true, text: data.y_label, font: { size: 12 } } : { display: false }

    const xAxis = { title: xTitle, ticks: { font: { size: 13 } }, grid: { display: false } }
    const yAxis = { title: yTitle, ticks: { font: { size: 13 } }, grid: { color: "#f3f4f6" } }

    if (kind === "pie") {
      return {
        type: "pie",
        data: { labels: data.labels, datasets: datasets },
        options: this._commonOptions()
      }
    }
    if (kind === "line" || kind === "sparkline_cell") {
      return {
        type: "line",
        data: { labels: data.labels, datasets: datasets },
        options: {
          ...this._commonOptions(),
          ...(kind === "sparkline_cell"
            ? { scales: { x: { display: false }, y: { display: false } }, plugins: { legend: { display: false } } }
            : { scales: { x: xAxis, y: yAxis } })
        }
      }
    }
    if (kind === "stacked_bar") {
      return {
        type: "bar",
        data: { labels: data.labels, datasets: datasets },
        options: {
          ...this._commonOptions(),
          scales: {
            x: { ...xAxis, stacked: true },
            y: { ...yAxis, stacked: true, beginAtZero: true }
          }
        }
      }
    }
    return {
      type: "bar",
      data: { labels: data.labels, datasets: datasets },
      options: {
        ...this._commonOptions(),
        scales: {
          x: xAxis,
          y: { ...yAxis, beginAtZero: true }
        }
      }
    }
  }

  _commonOptions() {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: {
          position: "bottom",
          labels: { boxWidth: 6, boxHeight: 6, font: { size: 13 }, usePointStyle: true }
        },
        title: { display: !!this.titleValue, text: this.titleValue, font: { size: 13 } }
      }
    }
  }
}
